import { putAndWaitAllAPI, selectAPI } from '@api/API.sagas';
import {
    API_GET_ACCOUNTS,
    API_GET_ACCOUNTS_SUCCESS,
    API_GET_CUSTOMER_STATE,
    API_GET_DRAW_DOWN_REPAYMENT_SCHEDULE,
    API_GET_DRAW_DOWN_REPAYMENT_SCHEDULE_SUCCESS,
    API_GET_FORMAL_OFFERS,
    API_GET_PAYMENT_METHOD,
    API_GET_PAYMENT_METHOD_SUCCESS,
    API_GET_RBL_ESTIMATED_SCHEDULE,
    API_GET_RBL_ESTIMATED_SCHEDULE_SUCCESS,
    API_POST_FUNDING_REQUEST,
    API_POST_FUNDING_REQUEST_FAIL,
    API_POST_FUNDING_REQUEST_SUCCESS,
    API_POST_FUND_PRODUCT_OFFER_REQUEST,
    API_POST_FUND_PRODUCT_OFFER_REQUEST_FAIL,
    API_POST_FUND_PRODUCT_OFFER_REQUEST_SUCCESS
} from '@api/lendingAPI/LendingAPI.actions';
import { CLOSE_MODAL } from '@iwModals/Modals.actions';
import { tracking } from '@iwoca/frontend-tracking-library';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { delay } from 'redux-saga';
import {
    all,
    call,
    fork,
    put,
    race,
    select,
    take,
    takeLatest
} from 'redux-saga/effects';

import { DRAW_DOWN_CONFIRM_DATA } from '../../drawDown/DrawDown.actions';
import {
    PROCESSING_PAYMENT_MODAL_RETURN_TO_DASHBOARD,
    PROCESSING_PAYMENT_MODAL_TRY_AGAIN,
    RESET_PROCESSING_PAYMENT_MODAL,
    SHOW_PROCESING_PAYMENT_MODAL,
    SHOW_PROCESING_PAYMENT_MODAL_COMPLETED,
    SHOW_PROCESING_PAYMENT_MODAL_ERROR
} from '../../modals/processingPaymentModal/ProcessingPaymentModal.actions';
import {
    REVENUE_BASED_LOAN_PRODUCT_TYPE,
    getProductTypeFromOfferId
} from '../../multiLoanDashboard/accountsSelector';
import { ACTIVATE_CONFIRM_STEP, CONFIRM_FUNDING } from '../Funding.actions';

export const DRAW_DOWN_MODE = 'draw_down';

export const confirmFundingSagaWorkers = [
    fork(activateDrawdownConfirm),
    fork(confirmFunding)
];

function* activateDrawdownConfirm() {
    yield takeLatest(ACTIVATE_CONFIRM_STEP, function* (action) {
        const { amount, offerId, sweep } = action;

        const [customerStateResponse, formalOffersResponse] =
            yield putAndWaitAllAPI([
                { type: API_GET_CUSTOMER_STATE },
                { type: API_GET_FORMAL_OFFERS }
            ]);

        const customer_state = customerStateResponse.response.data;
        const formal_offers = formalOffersResponse.response.data;
        const siteContext = yield select(({ siteContext }) => siteContext);

        tracking.ampli.drawdownConfirmStepViewed({
            amount,
            formalOfferId: offerId
        });

        const productType = getProductTypeFromOfferId(formal_offers, offerId);

        if (offerId) {
            if (!sweep) {
                yield put({
                    type: API_GET_DRAW_DOWN_REPAYMENT_SCHEDULE,
                    productType,
                    offerId,
                    amount
                });
            } else {
                yield put({
                    type: API_GET_RBL_ESTIMATED_SCHEDULE,
                    offerId,
                    amount: amount,
                    sweepFraction: sweep
                });
            }
        }

        yield all([
            put({ type: API_GET_ACCOUNTS }),
            put({ type: API_GET_PAYMENT_METHOD })
        ]);

        if (offerId) {
            if (!sweep) {
                yield all([
                    take(API_GET_ACCOUNTS_SUCCESS),
                    take(API_GET_PAYMENT_METHOD_SUCCESS),
                    take(API_GET_DRAW_DOWN_REPAYMENT_SCHEDULE_SUCCESS)
                ]);
            } else {
                yield all([
                    take(API_GET_ACCOUNTS_SUCCESS),
                    take(API_GET_PAYMENT_METHOD_SUCCESS),
                    take(API_GET_RBL_ESTIMATED_SCHEDULE_SUCCESS)
                ]);
            }
        }

        const { payment_method } = yield selectAPI();

        yield put({
            type: DRAW_DOWN_CONFIRM_DATA,
            siteContext,
            customer_state,
            payment_method,
            drawDownAmount: amount
        });
    });
}

function* confirmFunding() {
    yield takeLatest(CONFIRM_FUNDING, function* (action) {
        const { offerId, amount, sweep } = action;
        const drawDown = yield select(({ drawDown }) => drawDown);

        const fundingAccountInformation =
            drawDown?.selectedFundingAccount?.bankDetails ?? {};
        const fundingLabel = drawDown?.selectedFunding?.label ?? {};

        tracking.ampli.drawdownConfirmClicked({
            amount: amount,
            formalOfferId: offerId
        });

        yield put({
            type: SHOW_PROCESING_PAYMENT_MODAL,
            amount: amount,
            paymentMethod: fundingLabel,
            mode: DRAW_DOWN_MODE,
            offerId: offerId
        });

        const results = yield all([
            call(
                handleFundingRequest,
                offerId,
                amount,
                sweep,
                fundingAccountInformation
            ),
            call(delay, 3000)
        ]);

        if (results[0].success) {
            tracking.ampli.drawdownCompleted({
                amount: amount,
                formalOfferId: offerId
            });
            yield put({ type: SHOW_PROCESING_PAYMENT_MODAL_COMPLETED });
        } else {
            yield put({ type: SHOW_PROCESING_PAYMENT_MODAL_ERROR });
        }

        const nextAction = yield take([
            PROCESSING_PAYMENT_MODAL_TRY_AGAIN,
            PROCESSING_PAYMENT_MODAL_RETURN_TO_DASHBOARD
        ]);

        switch (nextAction.type) {
            case PROCESSING_PAYMENT_MODAL_TRY_AGAIN:
                yield resetProcessingPaymentAndClose();
                break;
            case PROCESSING_PAYMENT_MODAL_RETURN_TO_DASHBOARD:
                yield resetProcessingPaymentAndClose();
                yield put({
                    type: REQUEST_CHANGE_ROUTER,
                    location: { pathname: '/' }
                });
                break;
        }
    });
}

function* handleFundingRequest(offerId, amount, sweep, fundingAccount) {
    if (sweep) {
        return yield* handleRBLFundingRequest(
            offerId,
            amount,
            sweep,
            fundingAccount
        );
    } else {
        return yield* _handleFundingRequest(offerId, amount, fundingAccount);
    }
}

function* handleRBLFundingRequest(offerId, amount, sweep, fundingAccount) {
    const body = {
        data: {
            cash_amount: amount,
            revenue_sweep_fraction: sweep,
            bank_details: fundingAccount
        }
    };
    yield put({
        type: API_POST_FUND_PRODUCT_OFFER_REQUEST,
        offerId,
        productType: REVENUE_BASED_LOAN_PRODUCT_TYPE,
        body
    });

    return yield race({
        success: take(API_POST_FUND_PRODUCT_OFFER_REQUEST_SUCCESS),
        fail: take(API_POST_FUND_PRODUCT_OFFER_REQUEST_FAIL)
    });
}

function* _handleFundingRequest(offerId, amount, fundingAccount) {
    const { formal_offers } = yield selectAPI();

    const body = JSON.stringify({
        data: {
            cash_amount: amount,
            bank_details: fundingAccount
        }
    });
    const productType = getProductTypeFromOfferId(formal_offers, offerId);

    yield put({
        type: API_POST_FUNDING_REQUEST,
        body,
        offerId,
        productType
    });
    return yield race({
        success: take(API_POST_FUNDING_REQUEST_SUCCESS),
        fail: take(API_POST_FUNDING_REQUEST_FAIL)
    });
}

function* resetProcessingPaymentAndClose() {
    yield put({ type: RESET_PROCESSING_PAYMENT_MODAL });
    yield put({ type: CLOSE_MODAL });
}
